import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { sankey as d3Sankey, sankeyLinkHorizontal } from "d3-sankey";

export default function MembershipRetention({ data, selectedPlans, setSelectedPlans }) {
  const svgRef = useRef();

  const uniquePlans = data
    ? [...new Set(data.flatMap((d) => [d.plan_from, d.plan_to]))]
    : [];

  useEffect(() => {
    if (!data) return;

    const { nodes, links } = buildSankeyData(data, selectedPlans); // 👈 pass selectedPlans

    const months = [...new Set(nodes.map((n) => n.month))];
    const monthWidth = 250;
    const width = months.length * monthWidth;
    const height = 600;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const order = ["Premium", "Plus", "Lite", "Others", "No Plan"];

    const sankey = d3Sankey()
      .nodeId((d) => d.id)
      .nodeWidth(20)
      .nodePadding(20)
      .nodeSort((a, b) => order.indexOf(a.name) - order.indexOf(b.name))
      .extent([[0, 0], [width, height]]);

    const graph = sankey({
      nodes: nodes.map((d) => ({ ...d })),
      links: links.map((d) => ({ ...d }))
    });

    // Nodes
    svg.append("g")
      .selectAll("rect")
      .data(graph.nodes)
      .join("rect")
      .attr("x", (d) => d.x0)
      .attr("y", (d) => d.y0)
      .attr("height", (d) => d.y1 - d.y0)
      .attr("width", (d) => d.x1 - d.x0)
      .attr("fill", (d) => colorScale(d.name))
      .append("title")
      .text((d) => `${d.name} (${d.month}): ${d.value || 0}`);

    // Links
    svg.append("g")
      .attr("fill", "none")
      .selectAll("path")
      .data(graph.links)
      .join("path")
      .attr("d", sankeyLinkHorizontal())
      .attr("stroke", "#9ca3af")
      .attr("stroke-opacity", 0.5)
      .attr("stroke-width", (d) => Math.max(1, d.width))
      .append("title")
      .text(
        (d) =>
          `${d.source.name} (${d.source.month}) → ${d.target.name} (${d.target.month}): ${d.value}`
      );

    // Labels
    svg.append("g")
      .selectAll("text")
      .data(graph.nodes)
      .join("text")
      .attr("x", (d) => d.x0 - 6)
      .attr("y", (d) => (d.y0 + d.y1) / 2)
      .attr("dy", "0.35em")
      .attr("text-anchor", "end")
      .text((d) => `${d.name} (${d.month})`)
      .filter((d) => d.x0 < width / 2)
      .attr("x", (d) => d.x1 + 6)
      .attr("text-anchor", "start");
  }, [data, selectedPlans]); // 👈 re-render on selection change

  return (
    <>  
      <div style={{ marginBottom: "10px" }}>
        {uniquePlans.map((plan) => (
          <label key={plan} style={{ marginRight: "12px" }}>
            <input
              type="checkbox"
              checked={selectedPlans.includes(plan)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedPlans((prev) => [...prev, plan]);
                } else {
                  setSelectedPlans((prev) =>
                    prev.filter((p) => p !== plan)
                  );
                }
              }}
            />
            <span style={{ marginLeft: "4px", color: colorScale(plan) }}>
              {plan}
            </span>
          </label>
        ))}
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #ccc" }}>
        <svg ref={svgRef}></svg>
      </div>
    </>
  );
}

const colorScale = d3.scaleOrdinal()
  .domain(["Premium", "Plus", "Lite", "No Plan", "Others"])
  .range(["#fbbf24", "#3b82f6", "#10b981", "#ef4444", "#9ca3af"]);

  function buildSankeyData(rows, selectedPlans = []) {
  const nodes = [];
  const nodeMap = new Map();
  const links = [];
  const linkMap = new Map();

  function normalizePlan(plan) {
    if (selectedPlans.length === 0) return plan;
    return selectedPlans.includes(plan) ? plan : "Others";
  }

  function getNodeId(month, plan) {
    return `${plan}_${month}`;
  }

  rows.forEach((row) => {
    const planFrom = normalizePlan(row.plan_from);
    const planTo = normalizePlan(row.plan_to);

    const sourceId = getNodeId(row.month_from, planFrom);
    const targetId = getNodeId(row.month_to, planTo);
    const key = `${sourceId}->${targetId}`;

    if (!nodeMap.has(sourceId)) {
      nodeMap.set(sourceId, { id: sourceId, name: planFrom, month: row.month_from });
    }
    if (!nodeMap.has(targetId)) {
      nodeMap.set(targetId, { id: targetId, name: planTo, month: row.month_to });
    }

    if (!linkMap.has(key)) {
      linkMap.set(key, { source: sourceId, target: targetId, value: 0 });
    }
    linkMap.get(key).value += row.users_count;
  });

  return {
    nodes: Array.from(nodeMap.values()),
    links: Array.from(linkMap.values())
  };
}
